if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        // `this.mql` used to be sampled exactly once, so crossing 750px left the gallery keeping
        // the wrong semantics in both directions until reload: a thumbnail layout that was being
        // treated as a carousel (or vice versa) kept announcing itself that way. Re-evaluate
        // whenever the breakpoint moves.
        this.onBreakpointChange = this.onBreakpointChange.bind(this);
        this.mql.addEventListener('change', this.onBreakpointChange);

        this.setupDesktopNav();
        this.updateListSemantics();

        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
      }

      onBreakpointChange() {
        this.updateListSemantics();
        // The desktop nav only renders for the stacked layout, but the counter has to be
        // recomputed anyway: a resize can change which slides are on screen together.
        this.updateDesktopNavPosition();
      }

      /*
       * Keep the slider's list semantics honest.
       *
       * A thumbnail layout lays the media out as a wrapped grid rather than a carousel, so the
       * <ul>/<li> pairs are presentational. But product-media-gallery.liquid hard-coded
       * role="list" on the <ul>, so removing the child roles left an announced list whose every
       * <li> was role="presentation" -- announced as a list, empty when read. Both halves now come
       * from one predicate, and it is re-evaluated on breakpoint changes.
       *
       * Removing the attribute (rather than swapping in role="list") restores the native list
       * semantics of <ul>/<li>, which is what the markup already implies.
       */
      updateListSemantics() {
        const slider = this.elements.viewer?.slider;
        if (!slider) return;

        const isCarousel = !this.dataset.desktopLayout.includes('thumbnail') || !this.mql.matches;
        if (isCarousel) {
          slider.removeAttribute('role');
          this.elements.viewer.sliderItems.forEach((slide) => slide.removeAttribute('role'));
          return;
        }
        this.removeListSemantic();
      }

      onSlideChanged(event) {
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      /*
       * Desktop navigation for the stacked layout (see snippets/product-media-gallery.liquid).
       *
       * Only rendered when there is more than one media and the desktop layout has no thumbnail
       * rail of its own, so nothing here duplicates an existing control.
       *
       * The counter is derived from an IntersectionObserver rather than a scroll offset: the
       * stacked layout is a 2-up wrapping grid, so its scrollLeft never changes and the counter in
       * assets/global.js -- which computes itself from scrollLeft -- can only ever read 1 there.
       */
      setupDesktopNav() {
        this.desktopNav = this.querySelector('[data-media-desktop-nav]');
        if (!this.desktopNav) return;

        this.desktopNavCurrent = this.desktopNav.querySelector('[data-media-desktop-position-current]');
        this.desktopNavPrev = this.desktopNav.querySelector('[data-media-desktop-nav-prev]');
        this.desktopNavNext = this.desktopNav.querySelector('[data-media-desktop-nav-next]');
        this.desktopSlides = Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]') || []);
        if (!this.desktopNavCurrent || !this.desktopSlides.length) return;

        this.desktopNavPrev.addEventListener('click', () => this.stepDesktopNav(-1));
        this.desktopNavNext.addEventListener('click', () => this.stepDesktopNav(1));

        // A band across the middle of the viewport: whichever slides intersect it are the ones the
        // shopper is actually looking at. Where two share a row in the 2-up grid, the first in DOM
        // order wins, so the number never flickers between the two.
        this.desktopObserver = new IntersectionObserver(this.onDesktopNavIntersect.bind(this), {
          rootMargin: '-45% 0px -45% 0px',
        });
        this.desktopSlides.forEach((slide) => this.desktopObserver.observe(slide));

        this.updateDesktopNavPosition();
      }

      onDesktopNavIntersect(entries) {
        const visible = entries.filter((entry) => entry.isIntersecting);
        if (!visible.length) return;

        const first = visible.reduce((lowest, entry) =>
          this.desktopSlides.indexOf(entry.target) < this.desktopSlides.indexOf(lowest.target) ? entry : lowest
        );
        this.setDesktopNavPosition(this.desktopSlides.indexOf(first.target) + 1);
      }

      // Re-read from the is-active class rather than from the observer's last report: a click and
      // the observer can disagree for a frame, and the click is the more recent intent.
      updateDesktopNavPosition() {
        if (!this.desktopSlides?.length) return;
        const activeIndex = this.desktopSlides.findIndex((slide) => slide.classList.contains('is-active'));
        this.setDesktopNavPosition(activeIndex === -1 ? 1 : activeIndex + 1);
      }

      setDesktopNavPosition(position) {
        if (!this.desktopNavCurrent) return;
        this.desktopNavCurrent.textContent = position;
        this.desktopNavPrev.toggleAttribute('disabled', position <= 1);
        this.desktopNavNext.toggleAttribute('disabled', position >= this.desktopSlides.length);
      }

      stepDesktopNav(direction) {
        const current = parseInt(this.desktopNavCurrent.textContent, 10) || 1;
        const next = Math.min(Math.max(current + direction, 1), this.desktopSlides.length);
        const slide = this.desktopSlides[next - 1];
        if (!slide) return;

        // setActiveMedia owns the is-active class, the deferred media and the live-region
        // announcement; reusing it keeps all three in one place. It deliberately does not scroll a
        // slide that is below the fold, so the scroll is this method's job.
        this.setActiveMedia(slide.dataset.mediaId, false);
        this.setDesktopNavPosition(next);
        slide.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        // preventScroll: the scroll above is already where focus belongs, and letting the browser
        // scroll again would fight it.
        slide.querySelector('.product__media-toggle')?.focus({ preventScroll: true });
      }

      /*
       * Announces the newly active slide.
       *
       * Was `image.onload = ...; image.src = image.src;`. Re-assigning src is a no-op for an image
       * the browser has already decoded -- complete is true and `load` will not fire again -- so the
       * announcement was dropped exactly when the shopper was fastest, on a cached or
       * already-loaded image. Where it did fire, re-running src selection discarded the candidate
       * the browser had already picked from `srcset` and could swap in a different resolution,
       * which is a visible reflow on the slide the shopper just clicked.
       *
       * Now: announce at once if the image is already complete, otherwise once on load. src is
       * never touched, so the browser keeps its own selection.
       *
       * The old selector only matched `.product__modal-opener--image`, so moving to a video or 3D
       * model slide announced nothing. The preview image lives inside the modal opener for every
       * media type, so matching that container covers all of them.
       */
      announceLiveRegion(activeItem, position) {
        if (!position || !this.elements.liveRegion) return;
        const image = activeItem.querySelector('.product__modal-opener .product__media img');
        if (!image) return;

        if (image.complete) {
          this.announceMediaAvailable(position);
          return;
        }
        image.addEventListener('load', () => this.announceMediaAvailable(position), { once: true });
      }

      announceMediaAvailable(position) {
        // mediaAvailable covers image, video and 3D; imageAvailable is the image-only wording that
        // is published today, so it is the fallback until the shared string exists.
        const template =
          window.accessibilityStrings?.mediaAvailable || window.accessibilityStrings?.imageAvailable;
        if (!template) return;

        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.textContent = template.replace('[index]', position);
        window.setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
